import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Globe, Edit3, Plus, Trash2, Info, ChevronRight, Zap } from "lucide-react";
import scenarioService from "@/api/scenarioService";
import type { Scenario, CreateScenarioPayload } from "@/api/scenarioService";

const INDUSTRIES = [
  "Technology / SaaS","Financial Services","Healthcare","Real Estate / Mortgage",
  "Consumer Sales","Home Services","Automotive","Insurance","Professional Services",
  "Manufacturing / Industrial","Education / Training","Other",
];
const MEETING_TYPES = [
  { value: "call", label: "Cold Call" },
  { value: "demo", label: "Demo" },
  { value: "negotiation", label: "Negotiation" },
  { value: "objection", label: "Objection Handling" },
];

const inputCls = "w-full rounded-2xl border border-white/[0.08] bg-[#0A0A0A] text-white text-[14px] placeholder-gray-700 px-4 py-3.5 outline-none focus:border-[#B8F890]/40 focus:bg-[#111111] transition-all shadow-inner";
const labelCls = "block text-[16px]    text-[#fff] font-black mb-2 px-1" ;

type Tab = "manual" | "ai" | "url";

interface Props {
  existing: Scenario | null;
  repContext?: { id: string; name: string } | null;
  onClose: () => void;
  onSaved: (s: Scenario) => void;
}

export default function ScenarioFormModal({ existing, repContext, onClose, onSaved }: Props) {
  const isEdit = !!existing && !!existing.id;
  const [tab, setTab] = useState<Tab>("manual");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<Partial<Scenario> | null>(null);
  const [publishAction, setPublishAction] = useState<"publish_only" | "publish_assign">("publish_only");

  // Manual form state
  const [form, setForm] = useState<CreateScenarioPayload>({
    title: "", description: "", industry: INDUSTRIES[0], difficulty: "MEDIUM",
    avgDurationMin: 10, personaTitle: "", personaStyle: "", meetingType: "call",
    keyObjections: ["", "", ""], scriptHints: "",
  });

  // AI tab
  const [aiForm, setAiForm] = useState({ industry: INDUSTRIES[0], meetingType: "call", difficulty: "MEDIUM", additionalContext: "" });
  // URL tab
  const [urlForm, setUrlForm] = useState({ url: "", meetingType: "call", difficulty: "MEDIUM" });

  useEffect(() => {
    if (existing) {
      setForm({
        title: existing.title, description: existing.description || "",
        industry: existing.industry, difficulty: existing.difficulty,
        avgDurationMin: existing.avgDurationMin, personaTitle: existing.personaTitle || "",
        personaStyle: existing.personaStyle || "", meetingType: existing.meetingType,
        keyObjections: existing.keyObjections.length >= 3 ? existing.keyObjections : [...existing.keyObjections, "", ""].slice(0,3),
        scriptHints: existing.scriptHints || "",
      });
    }
  }, [existing]);

  const setField = (k: keyof CreateScenarioPayload, v: any) =>
    setForm(f => ({ ...f, [k]: v }));

  const setObjection = (i: number, val: string) => {
    const arr = [...form.keyObjections];
    arr[i] = val;
    setField("keyObjections", arr);
  };
  const addObjection = () => setField("keyObjections", [...form.keyObjections, ""]);
  const removeObjection = (i: number) =>
    setField("keyObjections", form.keyObjections.filter((_, idx) => idx !== i));

  const applyPreview = (p: Partial<Scenario>) => {
    setForm({
      title: p.title || "", description: p.description || "",
      industry: p.industry || INDUSTRIES[0], difficulty: p.difficulty || "MEDIUM",
      avgDurationMin: p.avgDurationMin || 10, personaTitle: p.personaTitle || "",
      personaStyle: p.personaStyle || "", meetingType: p.meetingType || "call",
      keyObjections: p.keyObjections && p.keyObjections.length >= 3 ? p.keyObjections : [...(p.keyObjections || []), "", ""].slice(0,3),
      scriptHints: p.scriptHints || "",
    });
    setPreview(null);
    setTab("manual");
  };

  const handleGenAI = async () => {
    setGenerating(true); setError("");
    try {
      const res = await scenarioService.generateWithAI({ ...aiForm, saveImmediately: false });
      setPreview(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || "AI generation failed");
    } finally { setGenerating(false); }
  };

  const handleGenUrl = async () => {
    if (!urlForm.url) { setError("Please enter a URL"); return; }
    setGenerating(true); setError("");
    try {
      const res = await scenarioService.generateFromUrl({ ...urlForm, saveImmediately: false });
      setPreview(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || "URL scraping failed");
    } finally { setGenerating(false); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError("Title is required"); return; }
    const validObjections = form.keyObjections.filter(o => o.trim());
    if (validObjections.length < 2) { setError("At least 2 objection variants are required"); return; }

    setSaving(true); setError("");
    try {
      const payload = { ...form, keyObjections: validObjections };
      const saved = isEdit
        ? await scenarioService.updateScenario(existing!.id, payload)
        : await scenarioService.createScenario(payload);
        
      if (!isEdit && repContext && publishAction === "publish_assign") {
        await scenarioService.assignScenario(saved.id, { userIds: [repContext.id] });
      }

      onSaved(saved);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to save scenario");
    } finally { setSaving(false); }
  };

  const TABS: { id: Tab; label: string; icon: any }[] = [
    { id: "manual", label: "Manual", icon: Edit3 },
    { id: "ai", label: "AI Designer", icon: Sparkles },
    { id: "url", label: "From URL", icon: Globe },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-md" onClick={onClose}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-[#13111F] border border-white/[0.08] rounded-[2.5rem] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" 
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-white/[0.06] bg-[#1A1726]/30">
          <div>
            <h2 className="text-white font-black text-xl " style={{fontWeight:"700"}}>{isEdit ? "Edit Scenario" : "New Practice Scenario"}</h2>
            <p className="text-[14px] text-[#fff] font-medium mt-0.5">Define the mission, buyer persona, and objections.</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-[#fff] hover:text-white transition-all">
            <X size={20} />
          </button>
        </div>

        {/* Tabs (only when creating) */}
        {!isEdit && (
          <div className="flex px-6 pt-4 gap-2 bg-[#1A1726]/20">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setError(""); setPreview(null); }}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-[14px] font-black rounded-t-2xl transition-all   ${tab === t.id ? "bg-[#13111F] text-[#B8F890] border-t border-x border-white/[0.08]" : "text-[#fff] hover:text-gray-400"}`} style={{fontWeight:"600"}}
              >
                <t.icon size={14} />
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="p-8 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
          {error && (
            <motion.div 
              initial={{ opacity: 0, x: -10 }} 
              animate={{ opacity: 1, x: 0 }}
              className="rounded-2xl border border-rose-500/20 bg-rose-500/8 px-5 py-4 text-rose-300 text-[13px] flex items-center gap-3"
            >
              <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
              {error}
            </motion.div>
          )}

          {/* Preview banner */}
          <AnimatePresence>
            {preview && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-[2rem] border border-[#B8F890]/20 bg-[#B8F890]/5 p-6 space-y-4 relative overflow-hidden group" style={{fontWeight:"500"}}
              >
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                   <Sparkles size={60} className="text-[#B8F890]"  style={{fontWeight:"500"}}/>
                </div>
                <div>
                  <p className="text-[#B8F890] text-[11px] font-black  tracking-widest mb-1 flex items-center gap-2" style={{fontWeight:"500"}}>
                    <Sparkles size={14} /> AI Draft Ready
                  </p>
                  <p className="text-white text-lg font-bold">{preview.title}</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => applyPreview(preview)} className="text-[11px] bg-[#B8F890] text-black font-black px-6 py-2.5 rounded-xl hover:bg-[#a0e070] transition-all  tracking-widest shadow-lg" style={{fontWeight:"500"}}>Use This Draft</button>
                  <button onClick={() => setPreview(null)} className="text-[11px] text-[#fff] border border-white/[0.08] px-6 py-2.5 rounded-xl hover:text-white transition-all  tracking-widest">Discard</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── AI Tab ── */}
          {tab === "ai" && !isEdit && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={{fontWeight:400}}>Vertical *</label>
                  <select value={aiForm.industry} onChange={e => setAiForm(f => ({ ...f, industry: e.target.value }))} className={inputCls} style={{fontWeight:400}}>
                    {INDUSTRIES.map(i => <option key={i} className="bg-[#0f0f0f]">{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={{fontWeight:400}}>Target Difficulty</label>
                  <select value={aiForm.difficulty} onChange={e => setAiForm(f => ({ ...f, difficulty: e.target.value }))} className={inputCls} style={{fontWeight:400}}>
                    <option value="EASY" className="bg-[#0f0f0f]">Easy</option>
                    <option value="MEDIUM" className="bg-[#0f0f0f]">Medium</option>
                    <option value="HARD" className="bg-[#0f0f0f]">Hard</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className={labelCls} style={{fontWeight:400}}>Engagement Type</label>
                  <select value={aiForm.meetingType} onChange={e => setAiForm(f => ({ ...f, meetingType: e.target.value }))} className={inputCls} style={{fontWeight:400}}>
                    {MEETING_TYPES.map(m => <option key={m.value} value={m.value} className="bg-[#0f0f0f]">{m.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls} style={{fontWeight:400}}>Mission Directives (Optional)</label>
                <textarea value={aiForm.additionalContext} onChange={e => setAiForm(f => ({ ...f, additionalContext: e.target.value }))} rows={4} placeholder="e.g. Focus on enterprise risk management, buyer is hesitant about implementation timelines..." className={inputCls + " resize-none"} style={{fontWeight:400}} />
              </div>
              <button 
                onClick={handleGenAI} 
                disabled={generating} 
                className="w-full flex items-center justify-center gap-3 py-4 bg-[#B8F890] text-black font-black rounded-2xl hover:bg-[#a0e070] transition-all disabled:opacity-50 text-[13px]  tracking-widest shadow-[0_0_30px_rgba(184,248,144,0.2)] uppercase" style={{fontWeight:"800"}}
              >
                {generating ? <><Zap className="w-4 h-4 animate-bounce" /> Brainstorming...</> : <><Sparkles className="w-4 h-4" /> Design Scenario</>}
              </button>
            </div>
          )}

          {/* ── URL Tab ── */}
          {tab === "url" && !isEdit && (
            <div className="space-y-6">
              <div className="bg-[#B8F890]/5 border border-[#B8F890]/10 rounded-2xl p-5 flex items-start gap-4" style={{fontWeight:"500"}}>
                 <Info size={24} className="text-[#B8F890] shrink-0 mt-1" style={{fontWeight:"500"}}/>
                 <p className="text-[13px] text-gray-400 leading-relaxed">
                   Simply provide a company's URL. Our AI will analyze their product, value proposition, and typical buyer profiles to craft a tailored practice session.
                 </p>
              </div>
              <div>
                <label className={labelCls} style={{fontWeight:400}}>Corporate Website *</label>
                <div className="relative group">
                   <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-[#fff] group-focus-within:text-[#B8F890] transition-colors" size={16} style={{fontWeight:"500"}}/>
                   <input value={urlForm.url} onChange={e => setUrlForm(f => ({ ...f, url: e.target.value }))} placeholder="https://company.com" className={inputCls + " pl-11"} style={{fontWeight:400}}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={{fontWeight:400}}>Scenario Type</label>
                  <select value={urlForm.meetingType} onChange={e => setUrlForm(f => ({ ...f, meetingType: e.target.value }))} className={inputCls} style={{fontWeight:400}}>
                    {MEETING_TYPES.map(m => <option key={m.value} value={m.value} className="bg-[#0f0f0f]">{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={{fontWeight:400}}>Difficulty</label>
                  <select value={urlForm.difficulty} onChange={e => setUrlForm(f => ({ ...f, difficulty: e.target.value }))} className={inputCls} style={{fontWeight:400}}>
                    <option value="EASY" className="bg-[#0f0f0f]">Easy</option>
                    <option value="MEDIUM" className="bg-[#0f0f0f]">Medium</option>
                    <option value="HARD" className="bg-[#0f0f0f]">Hard</option>
                  </select>
                </div>
              </div>
              <button 
                onClick={handleGenUrl} 
                disabled={generating} 
                className="w-full flex items-center justify-center gap-3 py-4 bg-[#B8F890] text-black font-black rounded-2xl hover:bg-[#a0e070] transition-all disabled:opacity-50 text-[13px]  tracking-widest shadow-[0_0_30px_rgba(184,248,144,0.2)] uppercase"  style={{fontWeight:"800"}}
              >
                {generating ? <><Globe className="w-4 h-4 animate-spin" /> Scraping Ecosystem...</> : <><Plus className="w-4 h-4" /> Import from Web</>}
              </button>
            </div>
          )}

          {/* ── Manual Form (also used after preview) ── */}
          {(tab === "manual" || isEdit) && (
            <div className="space-y-8 pb-4">
              <div className="space-y-4">
                <div>
                  <label className={labelCls} style={{fontWeight:400}}>Scenario Identity *</label>
                  <input value={form.title} onChange={e => setField("title", e.target.value)} placeholder="e.g. Inbound Demo: Enterprise AI Platform" className={inputCls} style={{fontWeight:400}} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} style={{fontWeight:400}}>Market Segment</label>
                    <select value={form.industry} onChange={e => setField("industry", e.target.value)} className={inputCls} style={{fontWeight:400}}>
                      {INDUSTRIES.map(i => <option key={i} className="bg-[#0f0f0f]">{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} style={{fontWeight:400}}>Complexity</label>
                    <select value={form.difficulty} onChange={e => setField("difficulty", e.target.value)} className={inputCls} style={{fontWeight:400}}>
                      <option value="EASY" className="bg-[#0f0f0f]">Easy</option>
                      <option value="MEDIUM" className="bg-[#0f0f0f]">Medium</option>
                      <option value="HARD" className="bg-[#0f0f0f]">Hard</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                 <p className="text-[14px]    text-[#B8F890] font-black border-l-2 border-[#B8F890] pl-3" style={{fontWeight:"500"}}>Buyer Architecture</p>
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls} style={{fontWeight:400}}>Persona Title</label>
                      <input value={form.personaTitle} onChange={e => setField("personaTitle", e.target.value)} placeholder="e.g. Chief Revenue Officer" className={inputCls} style={{fontWeight:400}} />
                    </div>
                    <div>
                      <label className={labelCls} style={{fontWeight:400}}>Target Duration (m)</label>
                      <input type="number" min={1} max={60} value={form.avgDurationMin} onChange={e => setField("avgDurationMin", parseInt(e.target.value) || 10)} className={inputCls} style={{fontWeight:400}}/>
                    </div>
                    <div className="col-span-2">
                      <label className={labelCls} style={{fontWeight:400}}>Behavioral Style</label>
                      <input value={form.personaStyle} onChange={e => setField("personaStyle", e.target.value)} placeholder="e.g. Highly analytical, impatient, focuses on bottom-line ROI" className={inputCls} style={{fontWeight:400}} />
                    </div>
                 </div>
              </div>

              <div className="space-y-4">
                <p className="text-[14px]   text-[#B8F890] font-black border-l-2 border-[#B8F890] pl-3" style={{fontWeight:"500"}}>Mission Intelligence</p>
                <div>
                  <label className={labelCls} style={{fontWeight:400}}>Detailed Background</label>
                  <textarea value={form.description} onChange={e => setField("description", e.target.value)} rows={3} placeholder="Provide context for the rep before they start the call..." className={inputCls + " resize-none"} style={{fontWeight:400}} />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3 px-1">
                    <label className={labelCls + " mb-0"} style={{fontWeight:400}}>Friction Variants * (Min 2)</label>
                    <button onClick={addObjection} className="flex items-center gap-1.5 text-[14px]  font-black text-[#B8F890] hover:text-[#a0e070] transition-colors" style={{fontWeight:"500"}}>+ New Variant</button>
                  </div>
                  <div className="space-y-3">
                    {form.keyObjections.map((obj, i) => (
                      <div key={i} className="flex items-center gap-3 group/obj">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#B8F890]/10 text-[#B8F890] text-[14px]  font-black shrink-0 border border-[#B8F890]/20" style={{fontWeight:"500"}}>
                           {i + 1}
                        </div>
                        <input value={obj} onChange={e => setObjection(i, e.target.value)} placeholder={`Potential pushback #${i + 1}`} className={inputCls} style={{fontWeight:400}} />
                        {form.keyObjections.length > 2 && (
                          <button onClick={() => removeObjection(i)} className="w-10 h-10 flex items-center justify-center rounded-xl text-rose-500 hover:bg-rose-500/10 transition-all opacity-0 group-hover/obj:opacity-100">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelCls} style={{fontWeight:400}}>Coach Directives (Optional)</label>
                  <textarea value={form.scriptHints} onChange={e => setField("scriptHints", e.target.value)} rows={3} placeholder="Guidance or key talking points for the AI coach to look for..." className={inputCls + " resize-none"} style={{fontWeight:400}}/>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {(tab === "manual" || isEdit) && (
          <div className="flex flex-col border-t border-white/[0.06] bg-[#1A1726]/30">
            {repContext && !isEdit && (
              <div className="px-10 py-5 border-b border-white/[0.06]">
                <p className="text-[14px] text-[#B8F890] font-black mb-3 uppercase tracking-widest">Deployment Options</p>
                <div className="flex flex-col gap-3">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${publishAction === 'publish_only' ? 'border-[#B8F890] bg-[#B8F890]/10' : 'border-white/[0.2] group-hover:border-white/[0.4]'}`}>
                      {publishAction === 'publish_only' && <div className="w-2.5 h-2.5 rounded-full bg-[#B8F890]" />}
                    </div>
                    <span className={`text-[14px] font-medium ${publishAction === 'publish_only' ? 'text-white' : 'text-[#fff]'}`}>Publish (makes it available but unassigned)</span>
                    <input type="radio" className="hidden" checked={publishAction === 'publish_only'} onChange={() => setPublishAction('publish_only')} />
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${publishAction === 'publish_assign' ? 'border-[#B8F890] bg-[#B8F890]/10' : 'border-white/[0.2] group-hover:border-white/[0.4]'}`}>
                      {publishAction === 'publish_assign' && <div className="w-2.5 h-2.5 rounded-full bg-[#B8F890]" />}
                    </div>
                    <span className={`text-[14px] font-medium ${publishAction === 'publish_assign' ? 'text-white' : 'text-[#fff]'}`}>Publish and Assign to {repContext.name}</span>
                    <input type="radio" className="hidden" checked={publishAction === 'publish_assign'} onChange={() => setPublishAction('publish_assign')} />
                  </label>
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-4 px-10 py-6">
              <button onClick={onClose} className="text-[12px] font-bold text-[#fff] hover:text-white transition-all tracking-widest uppercase px-4" style={{fontWeight:"800"}}>Cancel</button>
              <button 
                onClick={handleSave} 
                disabled={saving} 
                className="flex items-center uppercase gap-2 text-[12px] bg-[#B8F890] text-black font-black px-8 py-3.5 tracking-widest rounded-2xl hover:bg-[#a0e070] transition-all disabled:opacity-50 shadow-lg active:scale-95"
                style={{fontWeight:"800"}}
              >
                {saving ? "Synchronizing..." : isEdit ? "Update Scenario" : repContext ? (publishAction === "publish_assign" ? "Publish & Assign" : "Publish Scenario") : "Publish Scenario"}
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
